import React, { useState } from 'react';
import { useGenerationCosts } from '../hooks/useGenerationCosts';

export const GenerationCostManager: React.FC = () => {
  const { allCosts, loading, error, updateServiceCost, reloadCosts } = useGenerationCosts();
  const [updating, setUpdating] = useState<string | null>(null);
  const [updateError, setUpdateError] = useState<string | null>(null);
  const [updateSuccess, setUpdateSuccess] = useState<string | null>(null);

  const handleUpdateCost = async (serviceName: string, newCost: number) => {
    if (newCost < 0) {
      setUpdateError('Cost cannot be negative');
      return;
    }

    setUpdating(serviceName);
    setUpdateError(null);
    setUpdateSuccess(null);

    try {
      const success = await updateServiceCost(serviceName, newCost);
      if (success) {
        setUpdateSuccess(`Successfully updated ${serviceName} cost to ${newCost} tokens`);
        setTimeout(() => setUpdateSuccess(null), 3000);
      } else {
        setUpdateError(`Failed to update ${serviceName} cost`);
      }
    } catch (err) {
      setUpdateError(err instanceof Error ? err.message : 'Failed to update cost');
    } finally {
      setUpdating(null);
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold mb-4">Generation Costs</h3>
        <div className="text-center py-4">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-2 text-gray-600">Loading costs...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold mb-4">Generation Costs</h3>
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <p className="text-red-700">Error: {error}</p>
          <button 
            onClick={reloadCosts}
            className="mt-2 bg-red-600 hover:bg-red-700 text-white px-3 py-1 rounded text-sm"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-semibold">Generation Costs</h3>
        <button 
          onClick={reloadCosts}
          className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1 rounded text-sm"
        >
          Refresh
        </button>
      </div>

      {updateError && (
        <div className="mb-4 bg-red-50 border border-red-200 rounded-md p-3">
          <p className="text-red-700 text-sm">{updateError}</p>
        </div>
      )}

      {updateSuccess && (
        <div className="mb-4 bg-green-50 border border-green-200 rounded-md p-3">
          <p className="text-green-700 text-sm">{updateSuccess}</p>
        </div>
      )}

      <div className="space-y-4">
        {allCosts.map((cost) => (
          <div key={cost.service_name} className="border border-gray-200 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <h4 className="font-medium text-gray-900">{cost.service_display_name}</h4>
                <p className="text-sm text-gray-600 mt-1">{cost.description}</p>
                <p className="text-sm text-gray-500 mt-1">Service: {cost.service_name}</p>
              </div>
              
              <div className="flex items-center space-x-3">
                <div className="text-right">
                  <p className="text-sm text-gray-600">Current Cost</p>
                  <p className="text-lg font-semibold text-blue-600">{cost.base_cost} tokens</p>
                </div>
                
                <CostUpdateForm 
                  serviceName={cost.service_name}
                  currentCost={cost.base_cost}
                  onUpdate={handleUpdateCost}
                  isUpdating={updating === cost.service_name}
                />
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="mt-6 p-4 bg-gray-50 rounded-lg">
        <h4 className="font-medium text-gray-900 mb-2">Cost Management</h4>
        <p className="text-sm text-gray-600">
          Update token costs for AI generation services. Changes take effect immediately.
        </p>
      </div>
    </div>
  );
};

interface CostUpdateFormProps {
  serviceName: string;
  currentCost: number;
  onUpdate: (serviceName: string, newCost: number) => Promise<void>;
  isUpdating: boolean;
}

const CostUpdateForm: React.FC<CostUpdateFormProps> = ({ 
  serviceName, 
  currentCost, 
  onUpdate, 
  isUpdating 
}) => {
  const [newCost, setNewCost] = useState(currentCost.toString());
  const [showForm, setShowForm] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const cost = parseInt(newCost, 10);
    if (isNaN(cost)) return;
    
    await onUpdate(serviceName, cost);
    setShowForm(false);
  };

  const handleCancel = () => {
    setNewCost(currentCost.toString());
    setShowForm(false);
  };

  if (!showForm) {
    return (
      <button
        onClick={() => setShowForm(true)}
        disabled={isUpdating}
        className="bg-gray-600 hover:bg-gray-700 text-white px-3 py-1 rounded text-sm disabled:opacity-50"
      >
        Update
      </button>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="flex items-center space-x-2">
      <input
        type="number"
        value={newCost}
        onChange={(e) => setNewCost(e.target.value)}
        min="0"
        className="w-16 px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
        disabled={isUpdating}
      />
      <button
        type="submit"
        disabled={isUpdating}
        className="bg-green-600 hover:bg-green-700 text-white px-2 py-1 rounded text-xs disabled:opacity-50"
      >
        {isUpdating ? '...' : 'Save'}
      </button>
      <button
        type="button"
        onClick={handleCancel}
        disabled={isUpdating}
        className="bg-gray-500 hover:bg-gray-600 text-white px-2 py-1 rounded text-xs disabled:opacity-50"
      >
        Cancel
      </button>
    </form>
  );
}; 